import type { IDAChatMessage } from "./chatMessageMapper";
type Execution = NonNullable<IDAChatMessage["toolExecutions"]>[number];

/** Keep one chip at its first position; a terminal update wins over stale starts. */
export function reconcileToolChips(
  messages: IDAChatMessage[],
): IDAChatMessage[] {
  const rank = (execution: Execution) =>
    execution.status === "running"
      ? 0
      : execution.status === "pending_confirmation"
        ? 1
        : 2;
  const calls = new Map<string, Execution>();
  for (const message of messages)
    for (const execution of message.toolExecutions || []) {
      if (!execution.id) continue;
      const previous = calls.get(execution.id);
      if (!previous || rank(execution) >= rank(previous))
        calls.set(execution.id, execution);
    }
  const seen = new Set<string>();
  let changed = false;
  const result = messages.map((message) => {
    if (!message.toolExecutions?.length) return message;
    const executions: Execution[] = [];
    for (const execution of message.toolExecutions) {
      if (!execution.id) {
        executions.push(execution);
        continue;
      }
      if (seen.has(execution.id)) {
        changed = true;
        continue;
      }
      seen.add(execution.id);
      const canonical = calls.get(execution.id)!;
      if (canonical !== execution) changed = true;
      executions.push(
        canonical === execution
          ? execution
          : { ...canonical, contentPosition: execution.contentPosition },
      );
    }
    if (
      executions.length === message.toolExecutions.length &&
      executions.every(
        (execution, index) => execution === message.toolExecutions![index],
      )
    )
      return message;
    return { ...message, toolExecutions: executions };
  });
  return changed ? result : messages;
}
